import React, { useEffect } from 'react';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import { Checkbox } from '../../../../../../v2BaseComponents/Inputs';

const intl = getIntl('delete-client-prompt-content', {
    deleteClient: 'Delete Client{plural}',
    deleteClientPrompt:
        "Are you sure you want to delete {count} client{plural}?  This will delete all your organization's data for this client and you will no longer have access to this client's data.  The client's profile will not be deleted.  This action cannot be undone.",
    confirmation: 'I confirm that I want to delete {count} client{plural}.',
    delete: 'Delete',
    cancel: 'Cancel',
});

export type DeleteClientRecordsPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordIds: string[] | undefined;
    onDelete: (organizationClientRecordIds: string[]) => void;
};

export type DeleteClientRecordsPromptContentProps = DeleteClientRecordsPromptContentOwnProps;

const Content: React.FC<{
    clientRecordCount: number;
    onDeleteClients: () => void;
    onClose: () => void;
}> = ({ clientRecordCount, onDeleteClients, onClose }) => {
    const [isConsentChecked, setIsConsentChecked] = React.useState(false);
    const plural = clientRecordCount > 1 ? 's' : '';

    return (
        <ContentStack>
            <InfoIcon
                alertIconProps={{
                    name: 'Trash2',
                    variant: 'error',
                }}
                title={intl('deleteClient', { plural })}
                subText={intl('deleteClientPrompt', {
                    count: clientRecordCount,
                    plural,
                })}
            />
            <Checkbox
                value={isConsentChecked}
                onChange={setIsConsentChecked}
                title={intl('confirmation', {
                    count: clientRecordCount,
                    plural,
                })}
                testID="confirm-delete-clients-checkbox"
            />
            <PromptActions
                actions={[
                    {
                        title: intl('delete'),
                        onPress: onDeleteClients,
                        icon: 'Trash2',
                        variant: 'alert',
                        testID: 'confirm-delete-clients',
                        isDisabled: !isConsentChecked,
                    },
                    {
                        title: intl('cancel'),
                        onPress: onClose,
                        variant: 'secondary',
                        icon: 'XCircle',
                        testID: 'cancel-delete-clients',
                    },
                ]}
            />
        </ContentStack>
    );
};

const DeleteClientRecordsPromptContent: React.FC<DeleteClientRecordsPromptContentProps> = ({
    onClose,
    organizationClientRecordIds,
    onDelete,
}) => {
    useEffect(() => {
        if (!organizationClientRecordIds?.length) {
            onClose();
        }
    }, [organizationClientRecordIds, onClose]);

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={!organizationClientRecordIds?.length}>
                {() => (
                    <Content
                        clientRecordCount={organizationClientRecordIds?.length || 0}
                        onDeleteClients={() => {
                            onDelete(organizationClientRecordIds || []);
                        }}
                        onClose={onClose}
                    />
                )}
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(DeleteClientRecordsPromptContent);
